import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import { StoreContext, rootStore } from '@/stores'
import { Toaster } from '@/components/ui/sonner'
import { useEffect } from 'react'
import { useAuthStore, useTenantStore } from '@/stores'
import { AuthStatus } from '@/stores/AuthStore'
import MainLayout from '@/components/MainLayout'
import Login from '@/containers/auth/Login'
import TenantList from '@/containers/TenantList'
import TenantDetail from '@/containers/TenantDetail'
import RoomList from '@/containers/RoomList'
import RoomDetail from '@/containers/RoomDetail'
import GuestList from '@/containers/GuestList'
import GuestDetail from '@/containers/GuestDetail'
import LineSetting from '@/containers/LineSetting'
import UserList from '@/containers/UserList'
import UserDetail from '@/containers/UserDetail'
import ApiKeyList from '@/containers/ApiKeyList'

const PrivateRoute = observer(({ children }: { children: React.ReactNode }) => {
  const authStore = useAuthStore()
  
  // 初期状態の場合はローディング表示
  if (authStore.authStatus === AuthStatus.INITIAL) {
    return <div className="flex min-h-screen items-center justify-center">
      <div className="text-muted-foreground">Loading...</div>
    </div>
  }
  
  if (!authStore.isAuthenticated) {
    return <Navigate to="/auth/login" replace />
  }
  
  return <MainLayout>{children}</MainLayout>
})

const AppContent = observer(() => {
  const authStore = useAuthStore()
  const tenantStore = useTenantStore()

  useEffect(() => {
    const initialize = async () => {
      const success = await authStore.checkAuth()
      if (success && authStore.currentTenantId) {
        // ユーザーの所属テナント情報を取得してプランを設定
        await tenantStore.getTenant(authStore.currentTenantId)
      }
    }
    initialize()
  }, []) // 空の依存配列で初回のみ実行

  return (
    <>
      <Routes>
        <Route path="/auth/login" element={<Login />} />
        <Route
          path="/"
          element={
            <PrivateRoute>
              <div className="space-y-6 mb-6">
                <h1 className="text-3xl font-bold">ダッシュボード</h1>
                <p className="text-muted-foreground">
                  hogeAdmin へようこそ
                </p>
              </div>
            </PrivateRoute>
          }
        />
        <Route
          path="/tenants"
          element={
            <PrivateRoute>
              <TenantList />
            </PrivateRoute>
          }
        />
        <Route
          path="/tenants/:tenantId"
          element={
            <PrivateRoute>
              <TenantDetail />
            </PrivateRoute>
          }
        />
        <Route
          path="/tenants/:tenantId/rooms"
          element={
            <PrivateRoute>
              <RoomList />
            </PrivateRoute>
          }
        />
        <Route
          path="/tenants/:tenantId/rooms/:roomId"
          element={
            <PrivateRoute>
              <RoomDetail />
            </PrivateRoute>
          }
        />
        <Route
          path="/tenants/:tenantId/guests"
          element={
            <PrivateRoute>
              <GuestList />
            </PrivateRoute>
          }
        />
        <Route
          path="/tenants/:tenantId/guests/:guestId"
          element={
            <PrivateRoute>
              <GuestDetail />
            </PrivateRoute>
          }
        />
        <Route
          path="/tenants/:tenantId/settings/line"
          element={
            <PrivateRoute>
              <LineSetting />
            </PrivateRoute>
          }
        />
        <Route
          path="/tenants/:tenantId/users"
          element={
            <PrivateRoute>
              <UserList />
            </PrivateRoute>
          }
        />
        <Route
          path="/tenants/:tenantId/users/:userId"
          element={
            <PrivateRoute>
              <UserDetail />
            </PrivateRoute>
          }
        />
        <Route
          path="/tenants/:tenantId/keys"
          element={
            <PrivateRoute>
              <ApiKeyList />
            </PrivateRoute>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Toaster />
    </>
  )
})

function App() {
  return (
    <StoreContext.Provider value={rootStore}>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </StoreContext.Provider>
  )
}

export default App
